<!-- KNX heating rooms (Klima tab): summary tiles, the Ist / Soll overview, the rooms by floor and their popups -->

<!-- Loading: no KNX readings yet -->
<ng-container *ngIf="!summary">
  <div class="row g-2 mb-3">
    <!-- same grid as the tiles below -->
    <div *ngFor="let cls of ['col-12 col-sm-4', 'col-6 col-sm-4', 'col-6 col-sm-4', 'col-6 col-sm-6', 'col-6 col-sm-6']"
         class="col-lg" [ngClass]="cls">
      <div class="card h-100">
        <div class="card-body p-2 p-sm-3">
          <app-skeleton width="60%" height=".7rem" radius="4px" [darkMode]="darkMode" style="display:block;margin-bottom:.4rem;"></app-skeleton>
          <app-skeleton width="45%" height="1.4rem" radius="4px" [darkMode]="darkMode" style="display:block;"></app-skeleton>
        </div>
      </div>
    </div>
  </div>
  <div class="card mb-3">
    <div class="card-body p-2 p-sm-3">
      <app-skeleton width="100%" height="220px" radius="4px" [darkMode]="darkMode" style="display:block;"></app-skeleton>
    </div>
  </div>
  <!-- mobile view: list rows like the room list -->
  <div *ngIf="listView.matches" class="card dash-list mb-3">
    <div class="dash-row" *ngFor="let _ of [0,1,2,3,4,5]">
      <app-skeleton width="4px" height="32px" radius="2px" [darkMode]="darkMode" style="display:block;"></app-skeleton>
      <div class="dash-row-main">
        <app-skeleton width="45%" height=".9rem" radius="4px" [darkMode]="darkMode" style="display:block;margin-bottom:.3rem;"></app-skeleton>
        <app-skeleton width="70%" height=".7rem" radius="4px" [darkMode]="darkMode" style="display:block;"></app-skeleton>
      </div>
      <app-skeleton width="120px" height="28px" radius="4px" [darkMode]="darkMode" style="display:block;"></app-skeleton>
    </div>
  </div>
  <div *ngIf="!listView.matches" class="row g-2 mb-3">
    <div class="col-6 col-sm-4 col-lg-3" *ngFor="let _ of [0,1,2,3]">
      <div class="card">
        <div class="card-body p-2 p-sm-3">
          <div class="d-flex align-items-center gap-2 mb-2">
            <app-skeleton width="70%" height=".9rem" radius="4px" [darkMode]="darkMode" style="flex:1;"></app-skeleton>
            <app-skeleton width="2.4rem" height="1.2rem" radius="4px" [darkMode]="darkMode" style="flex-shrink:0;"></app-skeleton>
          </div>
          <app-skeleton width="100%" height="44px" radius="4px" [darkMode]="darkMode" style="display:block;"></app-skeleton>
        </div>
      </div>
    </div>
  </div>
</ng-container>

<ng-container *ngIf="summary as s">

  <div class="dash-section-head">
    <div class="dash-section-title">
      <span class="badge bg-danger dash-live"><span [style.opacity]="liveDotOn ? '1' : '0'">●</span> LIVE</span>
      <span class="dash-section-name">🔥 {{ 'KNX_ROOMS' | translate }}</span>
      <span *ngIf="knxUpdatedAt" class="dash-section-age">· {{ 'UPDATED_AGO' | translate:{time: updatedAgo(knxUpdatedAt)} }}</span>
    </div>
  </div>

  <!-- a) Summary tiles: Ø spans the phone width, then 2 per row; 3 per row on small, 5 on large screens -->
  <div class="row g-2 mb-3" [class.section-flash]="knxFlash">
    <div class="col-12 col-sm-4 col-lg">
      <div class="card h-100">
        <div class="card-body p-2 p-sm-3">
          <div class="knx-tile-label">{{ 'KNX_AVG_TEMP' | translate }}</div>
          <div class="knx-tile-value">{{ num(s.avgTempC) }}°</div>
          <div class="knx-tile-sub" *ngIf="s.avgSetpointC != null">Ø {{ 'TEMP_SETPOINT' | translate }} {{ num(s.avgSetpointC) }}°</div>
        </div>
      </div>
    </div>
    <div class="col-6 col-sm-4 col-lg">
      <div class="card h-100">
        <div class="card-body p-2 p-sm-3">
          <div class="knx-tile-label"><i class="knx-swatch" [style.background]="pal.below"></i>{{ 'KNX_BELOW_TARGET' | translate }}</div>
          <div class="knx-tile-value">{{ s.below }}<span class="knx-tile-sub fw-normal"> / {{ s.withSetpoint }}</span></div>
        </div>
      </div>
    </div>
    <div class="col-6 col-sm-4 col-lg">
      <div class="card h-100">
        <div class="card-body p-2 p-sm-3">
          <div class="knx-tile-label"><i class="knx-swatch" [style.background]="pal.above"></i>{{ 'KNX_ABOVE_TARGET' | translate }}</div>
          <div class="knx-tile-value">{{ s.above }}<span class="knx-tile-sub fw-normal"> / {{ s.withSetpoint }}</span></div>
        </div>
      </div>
    </div>
    <div class="col-6 col-sm-6 col-lg">
      <div class="card h-100">
        <div class="card-body p-2 p-sm-3">
          <div class="knx-tile-label">{{ 'KNX_COLDEST' | translate }}</div>
          <div class="knx-tile-value">{{ num(s.coldest.tempC) }}°</div>
          <div class="knx-tile-sub text-truncate m-wrap" [title]="roomLabel(s.coldest)">{{ roomLabel(s.coldest) }}</div>
        </div>
      </div>
    </div>
    <div class="col-6 col-sm-6 col-lg">
      <div class="card h-100">
        <div class="card-body p-2 p-sm-3">
          <div class="knx-tile-label">{{ 'KNX_WARMEST' | translate }}</div>
          <div class="knx-tile-value">{{ num(s.warmest.tempC) }}°</div>
          <div class="knx-tile-sub text-truncate m-wrap" [title]="roomLabel(s.warmest)">{{ roomLabel(s.warmest) }}</div>
        </div>
      </div>
    </div>
  </div>

  <!-- b) Overview: Ist per room as bars (coloured by delta to Soll), Soll as a tick marker -->
  <div class="card mb-3">
    <div class="card-body p-2 p-sm-3">
      <div class="d-flex align-items-center flex-wrap gap-2 mb-2">
        <span class="fw-semibold small">{{ 'KNX_OVERVIEW' | translate }}</span>
        <div class="d-flex flex-wrap align-items-center knx-legend text-muted ms-sm-auto">
          <span class="text-nowrap"><i class="knx-swatch" [style.background]="pal.below"></i>{{ 'KNX_BELOW_TARGET' | translate }}</span>
          <span class="text-nowrap"><i class="knx-swatch" [style.background]="pal.ok"></i>±{{ num(0.5) }}°</span>
          <span class="text-nowrap"><i class="knx-swatch" [style.background]="pal.above"></i>{{ 'KNX_ABOVE_TARGET' | translate }}</span>
          <span *ngIf="s.withSetpoint < rooms.length" class="text-nowrap"><i class="knx-swatch" [style.background]="pal.none" [style.box-shadow]="'inset 0 0 0 1px ' + pal.noneBorder"></i>{{ 'KNX_NO_TARGET' | translate }}</span>
          <span class="text-nowrap"><i class="knx-tick" [style.background]="pal.target"></i>{{ 'TEMP_SETPOINT' | translate }}</span>
          <span *ngIf="overviewHasValves" class="text-nowrap" [title]="'KNX_VALVE_OVERVIEW_HINT' | translate"><svg width="16" height="8" viewBox="0 0 16 8" style="margin-right:.3rem;vertical-align:0;"><rect x="0" y="2.5" width="16" height="3" rx="1.5" fill="currentColor" fill-opacity=".3"/><rect x="0" y="2.5" width="10" height="3" rx="1.5" fill="#0ca678"/></svg>{{ 'KNX_VALVE' | translate }} %</span>
        </div>
      </div>
      <div style="position:relative;" [style.height.px]="overviewHeight">
        <canvas baseChart
          type="bar"
          [data]="overviewData"
          [options]="overviewOptions"
          [plugins]="overviewPlugins">
        </canvas>
      </div>
    </div>
  </div>

  <!-- c) Rooms by floor: legend + range (phones: the range as a full-width segmented control below the legend) -->
  <div class="d-flex align-items-center gap-2 mb-2 flex-wrap">
    <div class="d-flex align-items-center knx-legend text-muted">
      <span class="text-nowrap">
        <svg width="16" height="8" viewBox="0 0 16 8" style="margin-right:.3rem;vertical-align:0;"><line x1="0" y1="4" x2="16" y2="4" [attr.stroke]="listView.matches ? sparkIstColor : '#0d6efd'" stroke-width="2"/></svg>{{ 'TEMP_ACTUAL' | translate }}
      </span>
      <span class="text-nowrap">
        <svg width="16" height="8" viewBox="0 0 16 8" style="margin-right:.3rem;vertical-align:0;"><line x1="0" y1="4" x2="16" y2="4" [attr.stroke]="setpointLineColor" stroke-width="2" stroke-dasharray="5 4"/></svg>{{ 'TEMP_SETPOINT' | translate }}
      </span>
      <!-- Heating valve position (right axis 0–100 %), for rooms with a mapped valve (list rows show it as "🔧 45 %") -->
      <span *ngIf="hasValves && !listView.matches" class="text-nowrap" [title]="'KNX_VALVE_LEGEND_HINT' | translate">
        <svg width="16" height="8" viewBox="0 0 16 8" style="margin-right:.3rem;vertical-align:0;"><rect x="0" y="2" width="16" height="6" fill="#0ca678" fill-opacity=".18"/><line x1="0" y1="2" x2="16" y2="2" stroke="#0ca678" stroke-width="1.5"/></svg>{{ 'KNX_VALVE' | translate }}
      </span>
    </div>
    <div class="knx-range d-flex align-items-center gap-2 ms-auto">
      <div class="btn-group btn-group-sm dash-seg dash-seg-xs" role="group">
        <button *ngFor="let r of chartRanges" type="button" class="btn"
                [ngClass]="range === r.key ? 'btn-secondary' : 'btn-outline-secondary'"
                [attr.aria-pressed]="range === r.key"
                (click)="selectRange(r.key)">{{ r.label | translate }}</button>
      </div>
      <span *ngIf="rangeLoading" class="spinner-border spinner-border-sm text-muted" role="status"></span>
    </div>
  </div>

  <!-- Mobile view (< 768 px): one list row per room (state bar, name, Soll / Δ / valve / humidity, sparkline, Ist);
       a tap opens the room's popup, the same "#klima/room/<deviceId>" deep link as a card -->
  <div *ngIf="listView.matches; else roomCardsTpl" class="card dash-list knx-room-list mb-3" [class.section-flash]="knxFlash">
    <ng-container *ngFor="let g of knxFloors; trackBy: trackFloor">
      <div *ngIf="g.floor" class="dash-list-group">{{ g.floor | knxName:lang:'floor' }}</div>
      <ng-container *ngFor="let t of g.rooms; trackBy: trackDevice">
        <ng-template #rowTapTpl>
          <ng-container *ngTemplateOutlet="chartTpl; context:{t: t}"></ng-container>
        </ng-template>
        <app-popup-card [bare]="true" cardClass="dash-row" popupMaxWidth="min(92vw, 1100px)"
                        [tapTpl]="rowTapTpl" [linkId]="roomPath(t.deviceId)" [popupTitle]="roomLabel(t)"
                        [boardChart]="'thermo.' + t.deviceId" (tappedChange)="chartOpened(t, $event)">
          <ng-template #body>
            <span class="knx-row-state" aria-hidden="true" [style.background]="rowStateColor(t)"
                  [class.knx-row-state-none]="t.setpointC == null"></span>
            <div class="dash-row-main">
              <div class="dash-row-title">{{ (t.room | knxName:lang:'room') ?? roomLabel(t) }}</div>
              <div class="dash-row-sub knx-row-sub">
                <span *ngIf="t.setpointC != null; else rowNoSollTpl">{{ 'TEMP_SETPOINT' | translate }} {{ num(t.setpointC) }}°
                  <span [ngClass]="deltaColor(t.tempC - t.setpointC)">({{ deltaLabel(t.tempC - t.setpointC) }})</span></span>
                <ng-template #rowNoSollTpl><span>{{ 'KNX_NO_TARGET' | translate }}</span></ng-template>
                <span *ngIf="t.valvePct != null"
                      [title]="('KNX_VALVE_TITLE' | translate:{pct: num(t.valvePct, 0)}) + (t.valveStatus === 'likely' ? ' · ' + ('KNX_VALVE_LIKELY_HINT' | translate) : '')">🔧&#8239;{{ num(t.valvePct, 0) }}&thinsp;%<span *ngIf="t.valveStatus === 'likely'" class="knx-valve-badge ms-1">{{ 'KNX_VALVE_LIKELY' | translate }}</span></span>
              </div>
            </div>
            <div class="dash-row-end">
              <svg *ngIf="roomSpark(t.deviceId) as sp" class="dash-row-spark" [attr.viewBox]="sparkViewBox" preserveAspectRatio="none" aria-hidden="true">
                <path *ngIf="sp.soll" [attr.d]="sp.soll" class="knx-spark-soll" [attr.stroke]="setpointLineColor"/>
                <path *ngIf="sp.ist" [attr.d]="sp.ist" class="knx-spark-ist" [attr.stroke]="sparkIstColor"/>
              </svg>
              <!-- the room's readings: Ist, and the humidity below it for rooms whose sensor sends one -->
              <span class="knx-row-vals">
                <span class="knx-row-temp" [ngClass]="tempColor(t.tempC)">{{ num(t.tempC) }}°</span>
                <span *ngIf="t.humidity != null" class="knx-row-hum" [ngClass]="humidityColor(t.humidity)"
                      [title]="('KNX_HUMIDITY' | translate) + ' ' + num(t.humidity, 0) + ' %'">💧{{ num(t.humidity, 0) }}&thinsp;%</span>
              </span>
            </div>
          </ng-template>
        </app-popup-card>
      </ng-container>
    </ng-container>
  </div>

  <!-- From 768 px: room cards grouped by floor (Ist + dashed Soll over the selected range) -->
  <ng-template #roomCardsTpl>
    <div class="mb-3">
      <ng-container *ngFor="let g of knxFloors; trackBy: trackFloor">
        <div *ngIf="g.floor" class="fw-semibold text-uppercase text-muted mb-1" style="font-size:.65rem;letter-spacing:.06em;">{{ g.floor | knxName:lang:'floor' }}</div>
        <div class="row g-2 mb-2" [class.section-flash]="knxFlash">
          <div class="col-6 col-sm-4 col-lg-3" *ngFor="let t of g.rooms; trackBy: trackDevice">
            <ng-template #knxTapTpl>
              <ng-container *ngTemplateOutlet="chartTpl; context:{t: t}"></ng-container>
            </ng-template>
            <!-- linkId: the popup is "#klima/room/<deviceId>" in the deep link (a link opens it once the card exists);
                 boardChart: its 📌 puts the room's chart on the Analyse board -->
            <app-popup-card popupMaxWidth="min(92vw, 1100px)" [tapTpl]="knxTapTpl" [linkId]="roomPath(t.deviceId)"
                            [popupTitle]="roomLabel(t)" [boardChart]="'thermo.' + t.deviceId"
                            (tappedChange)="chartOpened(t, $event)">
              <ng-template #body>
                <div class="card-body p-2 p-sm-3">
                  <div class="d-flex align-items-baseline thermo-row" style="gap:4px;">
                    <span class="fw-bold text-truncate" style="min-width:0;flex:1;">{{ (t.room | knxName:lang:'room') ?? roomLabel(t) }}</span>
                    <span class="text-nowrap fw-semibold" style="flex-shrink:0;font-size:1.25rem;line-height:1.1;"
                          [ngClass]="tempColor(t.tempC)">{{ num(t.tempC) }}°</span>
                    <!-- Relative humidity, for rooms whose KNX sensor also sends it -->
                    <span *ngIf="t.humidity != null" class="knx-hum" [ngClass]="humidityColor(t.humidity)"
                          [title]="('KNX_HUMIDITY' | translate) + ' ' + num(t.humidity, 0) + ' %'">💧{{ num(t.humidity, 0) }}&thinsp;%</span>
                  </div>
                  <div *ngIf="t.setpointC != null" class="d-flex justify-content-between text-nowrap" style="font-size:.7rem;line-height:1.3;">
                    <span class="text-muted">{{ 'TEMP_SETPOINT' | translate }} {{ num(t.setpointC) }}°</span>
                    <span [ngClass]="deltaColor(t.tempC - t.setpointC)">{{ deltaLabel(t.tempC - t.setpointC) }}</span>
                  </div>
                  <!-- Heating valve (Stellgröße) of the room: value + gauge, "vermutet" while only learned -->
                  <div *ngIf="t.valvePct != null" class="knx-valve"
                       [title]="('KNX_VALVE_TITLE' | translate:{pct: num(t.valvePct, 0)}) + (t.valveStatus === 'likely' ? ' · ' + ('KNX_VALVE_LIKELY_HINT' | translate) : '')">
                    <span class="knx-valve-num">🔧&#8239;{{ num(t.valvePct, 0) }}&thinsp;%</span>
                    <span class="knx-valve-gauge" aria-hidden="true"><span [style.width.%]="valveWidth(t.valvePct)"></span></span>
                    <span *ngIf="t.valveStatus === 'likely'" class="knx-valve-badge">{{ 'KNX_VALVE_LIKELY' | translate }}</span>
                  </div>
                  <ng-container *ngIf="sparklinePoints(t.deviceId).length >= 1">
                    <div class="d-flex align-items-center" style="margin-top:4px;">
                      <app-sparkline-chart
                        [points]="expandedPoints(t.deviceId)"
                        [valve]="valveFor(t)"
                        [darkMode]="darkMode"
                        [mode]="chartMode(t)"
                        [windowMs]="windowMs"
                        [numFmt]="numFn"
                        style="display:block;flex:1;min-width:0;">
                      </app-sparkline-chart>
                      <ng-container *ngIf="t.humidity != null">
                        <ng-container *ngTemplateOutlet="modeToggleTpl; context:{t: t}"></ng-container>
                      </ng-container>
                    </div>
                    <!-- min / max stay blank while the selected range has no points (loading / failed) -->
                    <div class="d-flex justify-content-between" style="font-size:.6rem;line-height:1;margin-top:1px;">
                      <span class="text-muted"><ng-container *ngIf="sparklineMinMax(t.deviceId) as mm">{{ mmLabel(t, mm, 'min') }}</ng-container></span>
                      <span class="text-muted">{{ rangeLabel }}</span>
                      <span class="text-muted"><ng-container *ngIf="sparklineMinMax(t.deviceId) as mm">{{ mmLabel(t, mm, 'max') }}</ng-container></span>
                    </div>
                  </ng-container>
                </div>
              </ng-template>
            </app-popup-card>
          </div>
        </div>
      </ng-container>
    </div>

  </ng-template>

</ng-container>

<!-- Expanded room chart (tap / click on a card or a list row; the room's name is the popup's title) -->
<ng-template #chartTpl let-t="t">
  <div class="d-flex align-items-center gap-2 mb-1 flex-wrap">
    <div class="min-w-0 knx-pop-info">
      <div class="text-muted">
        <span class="text-nowrap">{{ 'TEMP_ACTUAL' | translate }} {{ num(t.tempC) }}°</span><ng-container *ngIf="t.setpointC != null">
        · <span class="text-nowrap">{{ 'TEMP_SETPOINT' | translate }} {{ num(t.setpointC) }}°
        <span [ngClass]="deltaColor(t.tempC - t.setpointC)">({{ deltaLabel(t.tempC - t.setpointC) }})</span></span></ng-container><ng-container *ngIf="t.humidity != null">
        · <span class="text-nowrap">💧 {{ num(t.humidity, 0) }}&thinsp;%</span></ng-container>
      </div>
      <!-- Own line: the Ist/Soll line above already fills a phone-wide popup -->
      <div *ngIf="t.valvePct != null" class="text-muted d-flex align-items-center flex-wrap" style="gap:0 .35rem;">
        <span class="text-nowrap" [title]="'KNX_VALVE_TITLE' | translate:{pct: num(t.valvePct, 0)}">🔧 {{ 'KNX_VALVE' | translate }} {{ num(t.valvePct, 0) }}&thinsp;%</span>
        <span *ngIf="t.valveStatus === 'likely'" class="knx-valve-badge" [title]="'KNX_VALVE_LIKELY_HINT' | translate">{{ 'KNX_VALVE_LIKELY' | translate }}</span>
      </div>
    </div>
    <!-- Range (+ the humidity toggle): one full-width row on phones -->
    <div class="knx-pop-ctl d-flex align-items-center gap-2 ms-auto">
      <div class="btn-group btn-group-sm dash-seg" role="group">
        <button *ngFor="let r of chartRanges" type="button" class="btn"
                [ngClass]="range === r.key ? 'btn-secondary' : 'btn-outline-secondary'"
                [attr.aria-pressed]="range === r.key"
                (click)="selectRange(r.key)">{{ r.label | translate }}</button>
      </div>
      <span *ngIf="rangeLoading" class="spinner-border spinner-border-sm text-muted" role="status"></span>
      <ng-container *ngIf="t.humidity != null">
        <ng-container *ngTemplateOutlet="modeToggleTpl; context:{t: t}"></ng-container>
      </ng-container>
    </div>
  </div>
  <!-- Phones: the chart fills most of the sheet (dash-sheet-chart + sheetFill), compact legend / ticks -->
  <app-sparkline-chart
    class="dash-sheet-chart"
    [points]="expandedPoints(t.deviceId)"
    [valve]="valveFor(t)"
    [darkMode]="darkMode"
    [expanded]="true"
    [sheetFill]="true"
    [compactOnPhone]="true"
    [windowMs]="windowMs"
    [mode]="chartMode(t)"
    [numFmt]="numFn"
    [istTag]="adminIstGa(t)"
    [valveTag]="adminValveGa(t)"
    [linkId]="roomPath(t.deviceId)"
    style="display:block;">
  </app-sparkline-chart>
  <div class="text-muted text-center mt-1 knx-pop-meta">
    {{ expandedPoints(t.deviceId).length }} {{ 'MESSPUNKTE' | translate }}
  </div>

  <!-- Admin only (admin session held): the room's KNX details — actuator channel, the channel's group addresses
       with their last overheard value and sender, HomeServer data points, known anomalies. Read-only; never part
       of a TV snapshot (data-no-tv-snapshot, see assets/tv-snapshot-receiver.js). -->
  <div *ngIf="adminAuth" class="knx-adm mt-2" data-no-tv-snapshot>
    <div class="knx-adm-head">
      <span class="fw-semibold">📡 {{ 'KNX_ADMIN_DETAILS' | translate }}</span>
      <span class="knx-adm-badge" [title]="'KNX_ADMIN_ONLY_HINT' | translate">{{ 'KNX_ADMIN_BADGE' | translate }}</span>
      <span *ngIf="adminAsOf" class="knx-adm-sub ms-auto">{{ 'KNX_ADMIN_AS_OF' | translate:{time: clockTime(adminAsOf)} }}</span>
    </div>
    <div *ngIf="adminError" class="knx-adm-sub text-warning-emphasis">{{ 'KNX_ADMIN_ERROR' | translate:{error: adminError} }}</div>

    <ng-container *ngIf="adminRoom(t.deviceId) as k; else adminNoRoomTpl">
      <!-- Actuator channel (+ product) and the HomeServer's data points of this room -->
      <div *ngIf="k.actuatorIa || k.channel" class="knx-adm-sub"
           [title]="adminTableDate ? ('KNX_ADMIN_TABLE_HINT' | translate:{date: adminTableDate}) : ''">
        <span *ngIf="k.actuatorIa" class="text-nowrap">{{ 'ADMIN_KNX_VALVES_ACTUATOR' | translate:{ia: k.actuatorIa} }}</span><span *ngIf="k.channel" class="text-nowrap"><ng-container *ngIf="k.actuatorIa"> · </ng-container>{{ 'ADMIN_KNX_VALVES_CHANNEL' | translate:{n: k.channel} }}</span><ng-container *ngIf="k.product"> ({{ k.product }})</ng-container>
        <span *ngIf="k.pwm" class="knx-adm-badge ms-1" [title]="'ADMIN_KNX_VALVES_PWM_HINT' | translate">{{ 'KNX_ADMIN_PWM' | translate }}</span>
      </div>
      <div *ngIf="k.hs.length" class="knx-adm-sub">
        {{ 'KNX_ADMIN_HS' | translate }} <ng-container *ngFor="let p of k.hs; let last = last"><span class="text-nowrap">{{ p.key | translate:{v: p.v} }}</span><ng-container *ngIf="!last"> · </ng-container></ng-container>
      </div>
      <!-- Humidity of the room sensor, for rooms whose sensor also sends it -->
      <div *ngIf="k.humidityGa" class="knx-adm-sub">
        {{ 'KNX_ADMIN_HUMIDITY' | translate:{ga: k.humidityGa, sender: k.humiditySender || '?'} }}<ng-container *ngIf="k.humidityLikely"> ({{ 'KNX_VALVE_LIKELY' | translate }})</ng-container>
      </div>
      <!-- What the HomeServer shows vs. what the controller channel really controls to -->
      <div *ngIf="adminHsSet(k, t) != null || k.effSet != null" class="knx-adm-set" [title]="'KNX_ADMIN_SET_HINT' | translate">
        <span *ngIf="adminHsSet(k, t) != null" class="text-nowrap">{{ 'KNX_ADMIN_HS_SET' | translate:{v: num(adminHsSet(k, t)!) + ' °C'} }}</span>
        <span *ngIf="adminHsSet(k, t) != null && k.effSet != null" class="text-muted">/</span>
        <span *ngIf="k.effSet != null" class="text-nowrap fw-semibold"
              [class.text-warning-emphasis]="adminSetDiffers(k, t)">{{ 'KNX_ADMIN_EFFECTIVE_SET' | translate:{v: num(k.effSet) + ' °C'} }}</span>
      </div>

      <!-- The channel's group objects: name, GA, direction + write flag, last value (age), dominant sender -->
      <div *ngIf="k.rows.length" class="knx-adm-table">
        <div class="knx-adm-row knx-adm-th">
          <span>{{ 'KNX_ADMIN_COL_ROLE' | translate }}</span>
          <span>{{ 'KNX_ADMIN_COL_GA' | translate }}</span>
          <span>{{ 'KNX_ADMIN_COL_DIR' | translate }}</span>
          <span class="knx-adm-val">{{ 'KNX_ADMIN_COL_VALUE' | translate }}</span>
          <span>{{ 'KNX_ADMIN_COL_SENDER' | translate }}</span>
        </div>
        <div *ngFor="let g of k.rows; trackBy: trackAdminGa" class="knx-adm-row">
          <span class="knx-adm-c-role" [title]="g.roleHint ? (g.roleHint | translate) : ''">{{ g.role | translate }}<ng-container *ngIf="g.probable"> ({{ 'KNX_ADMIN_PROBABLE' | translate }})</ng-container><span *ngIf="g.badge" class="knx-adm-st">{{ g.badge | translate }}</span></span>
          <span class="knx-adm-c-ga knx-adm-mono" [title]="g.gaTitle">{{ g.ga }}</span>
          <span class="knx-adm-c-dir knx-adm-dir">
            <span *ngIf="g.dir">{{ g.dir | translate }}</span>
            <span *ngIf="g.writable === true" class="knx-adm-flag" [title]="'KNX_ADMIN_W_HINT' | translate">W</span>
            <span *ngIf="g.writable === false" class="knx-adm-flag off" [title]="'KNX_ADMIN_NO_W_HINT' | translate">{{ 'KNX_ADMIN_NO_W' | translate }}</span>
          </span>
          <span class="knx-adm-c-val knx-adm-val" [class.text-warning-emphasis]="g.warn" [class.fw-semibold]="g.warn">
            <ng-container *ngIf="g.value; else adminNoValueTpl">{{ g.value }}<span *ngIf="g.ts" class="knx-adm-age" [title]="fullTime(g.ts)"> · {{ updatedAgo(g.ts) }}</span></ng-container>
          </span>
          <span class="knx-adm-c-snd knx-adm-mono" [title]="g.senderTitle">{{ g.sender }}<span *ngIf="g.others" class="knx-adm-age"> {{ g.others }}</span></span>
        </div>
      </div>

      <ul *ngIf="k.notes.length" class="knx-adm-notes">
        <li *ngFor="let n of k.notes">⚠️ {{ n }}</li>
      </ul>
    </ng-container>
  </div>
</ng-template>

<ng-template #adminNoRoomTpl>
  <div *ngIf="!adminError" class="knx-adm-sub">{{ (adminLoaded ? 'KNX_ADMIN_NO_ROOM' : 'KNX_ADMIN_LOADING') | translate }}</div>
</ng-template>

<ng-template #adminNoValueTpl>
  <span class="text-muted" [title]="'KNX_ADMIN_NO_VALUE' | translate">–</span>
</ng-template>

<!-- Temperature ⇄ humidity toggle of a room chart (shows the icon of the other quantity) -->
<ng-template #modeToggleTpl let-t="t">
  <button type="button"
          (click)="toggleChartMode($event, t)"
          class="btn btn-link flex-shrink-0 d-flex align-items-center justify-content-center knx-mode-btn"
          [title]="'KNX_TOGGLE_HUMIDITY' | translate"
          [attr.aria-label]="'KNX_TOGGLE_HUMIDITY' | translate"
          [attr.aria-pressed]="chartMode(t) === 'humidity'">
    <svg *ngIf="chartMode(t) === 'temp'" xmlns="http://www.w3.org/2000/svg" width="1.35em" height="1.35em" fill="currentColor" viewBox="0 0 16 16">
      <path fill-rule="evenodd" d="M7.21.8C7.69.295 8 0 8 0c.109.845.468 1.69 1.036 2.572C10.055 4.218 11 5.445 11 7a3 3 0 0 1-6 0c0-1.555.95-2.78 1.964-4.428C7.15 1.486 7.21.8 7.21.8"/>
    </svg>
    <svg *ngIf="chartMode(t) === 'humidity'" xmlns="http://www.w3.org/2000/svg" width="1.35em" height="1.35em" fill="currentColor" viewBox="0 0 16 16">
      <path d="M9.5 12.5a1.5 1.5 0 1 1-2-1.415V6.5a.5.5 0 0 1 1 0v4.585a1.5 1.5 0 0 1 1 1.415"/>
      <path d="M5.5 2.5a2.5 2.5 0 0 1 5 0v7.55a3.5 3.5 0 1 1-5 0zM8 1a1.5 1.5 0 0 0-1.5 1.5v7.987l-.167.15a2.5 2.5 0 1 0 3.333 0L9.5 10.486V2.5A1.5 1.5 0 0 0 8 1"/>
    </svg>
  </button>
</ng-template>
